import isFunction from "lodash/isFunction";
import { useCallback, useContext, useEffect, useRef } from "react";
import { ZERO_DIGIT } from "../../../../../constants/env";
import { ChatbotContext } from "../../chatbot.context";
import { useChatbotMediator } from "../use-chatbot-manager/use-chatbot-mediator";
import type { UseChatbotMessagesAutoScrollParams } from "./use-chatbot-messages-auto-scroll.interface";

export function useChatbotMessagesAutoScroll({ chatContainerRef }: Readonly<UseChatbotMessagesAutoScrollParams>) {
  const { state } = useContext(ChatbotContext);
  const chatbotMediator = useChatbotMediator();

  const lastScrollTopValue = useRef(ZERO_DIGIT);

  const scrollToBottomCallback = useCallback(
    function scrollToBottomOfTheChat(behavior: ScrollBehavior = "smooth", force: boolean = false) {
      if (!chatContainerRef?.current || !isFunction(chatContainerRef.current.scrollTo)) {
        return;
      }

      const notEnoughContentToScroll = chatContainerRef.current.scrollHeight <= chatContainerRef.current.clientHeight;
      if (notEnoughContentToScroll) {
        return;
      }

      if (!force && state.showScrollToBottom) {
        return;
      }

      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior,
      });
    },
    [chatContainerRef, state.showScrollToBottom]
  );

  useEffect(
    function handleClientHeightChange() {
      if (!chatContainerRef?.current) {
        return;
      }
      const lastScrollTop = chatContainerRef.current.scrollTop;
      lastScrollTopValue.current = lastScrollTop;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chatContainerRef?.current?.clientHeight]
  );

  const checkIfUserIsScrolling = useCallback(
    function onContainerScrollCheckIfItWasTheUser() {
      if (!chatContainerRef?.current) {
        return;
      }
      const scrollTop = chatContainerRef.current.scrollTop;
      const scrollHeight = chatContainerRef.current.scrollHeight;
      const clientHeight = chatContainerRef.current.clientHeight;

      const increasedScrollTopToAllowUserDetection = scrollTop + 2;

      const shouldStopAutoScrolling =
        increasedScrollTopToAllowUserDetection < lastScrollTopValue.current && !state.showScrollToBottom;
      const shouldResumeAutoScrolling = scrollHeight - scrollTop <= clientHeight && state.showScrollToBottom;

      lastScrollTopValue.current = scrollTop;

      if (shouldStopAutoScrolling) {
        chatbotMediator.showScrollToBottomButton();
        return;
      }

      if (shouldResumeAutoScrolling) {
        chatbotMediator.hideScrollToBottomButton();
      }
    },

    [chatContainerRef, chatbotMediator, state.showScrollToBottom]
  );

  useEffect(
    function handleChatbotAnswerTypingScroll() {
      if (!chatContainerRef?.current) {
        return;
      }

      const currentContainer = chatContainerRef.current;
      let previousScrollHeight = currentContainer.scrollHeight;

      const observer = new MutationObserver(function handleScrollHeightIncrease() {
        const newScrollHeight = currentContainer.scrollHeight;
        const didContentHeightIncrease = newScrollHeight > previousScrollHeight;

        if (didContentHeightIncrease) {
          scrollToBottomCallback();
          previousScrollHeight = newScrollHeight;
        }
      });
      observer.observe(currentContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      return function disconnectObserverOnUnmount() {
        observer.disconnect();
      };
    },
    [chatContainerRef, scrollToBottomCallback]
  );

  useEffect(
    function goToBottomOnNewUserMessage() {
      if (!state.isSending) {
        return;
      }
      chatbotMediator.setScrollToBottomAction(true);
    },
    [chatbotMediator, state.isSending]
  );

  useEffect(
    function handleGoToBottom() {
      if (!state.scrollToBottom) {
        return;
      }
      chatbotMediator.setScrollToBottomAction(false);
      chatbotMediator.hideScrollToBottomButton();
      scrollToBottomCallback("smooth", true);
    },
    [chatbotMediator, scrollToBottomCallback, state.scrollToBottom]
  );

  return { handleContainerScroll: checkIfUserIsScrolling };
}
